import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, finalize, from, switchMap, catchError, of } from 'rxjs';
import { AuthService } from '../auth/auth';
import { LoaderService } from '../../core/loader/loader';
import { SKIP_LOADER } from './http-context';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private readonly correlationIdStorageKey = 'ai-qc-correlation-id';

  constructor(private auth: AuthService, private loaderService: LoaderService) {}

  private generateCorrelationId(): string {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }

    return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
  }

  private getOrCreateCorrelationId(): string {
    if (typeof sessionStorage === 'undefined') {
      return this.generateCorrelationId();
    }

    const existingId = sessionStorage.getItem(this.correlationIdStorageKey);
    if (existingId) {
      return existingId;
    }

    const generatedId = this.generateCorrelationId();
    sessionStorage.setItem(this.correlationIdStorageKey, generatedId);
    return generatedId;
  }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const showLoader = !req.context.get(SKIP_LOADER);
    if (showLoader) {
      this.loaderService.show();
    }
    const hideLoader = () => {
      if (showLoader) {
        this.loaderService.hide();
      }
    };
    const correlationId = req.headers.get('x-correlation-id') || this.getOrCreateCorrelationId();

    // Skip authorization if X-Skip-Auth header is present
    if (req.headers.has('X-Skip-Auth')) {
      const headers = req.headers
        .delete('X-Skip-Auth')
        .set('x-correlation-id', correlationId);
      const cloned = req.clone({ headers });
      return next.handle(cloned).pipe(
        finalize(() => hideLoader())
      );
    }

    return from(this.auth.getTokenSilent()).pipe(
      catchError(() => of(null)),
      switchMap((result: any) => {
        let cloned = req;
        const setHeaders: any = {
          'x-correlation-id': correlationId,
        };

        if (result?.idToken) {
          setHeaders['Authorization'] = `Bearer ${result.idToken}`;
        }

        cloned = req.clone({ setHeaders });

        return next.handle(cloned).pipe(
          finalize(() => hideLoader())
        );
      })
    );
  }
}
